import { CircularProgress } from '@mui/material'
import { FC, SVGProps, useState } from 'react'
import { AssessmentQuestion, SubmissionAnswer } from '../types'
import ZProgressBar from '../components/ZProgressBar'
import TreeQuestion from '../components/TreeQuestion'
import ZButton from '../components/ZButton'
import ZIcon from '../components/ZIcon'
import ZBottomContainer from '../components/ZBottomContainer'
import ZContainer from '../components/ZContainer'
import ZScreenContainer from '../components/ZScreenContainer'
import Header from '../components/Header'
import { AnswerMap } from '../util/categorisationTree'
import {
  AppropriatenessAssessmentQuestionOne,
  AppropriatenessAssessmentQuestionTwo,
  AppropriatenessAssessmentQuestionThree,
  AppropriatenessAssessmentQuestionFour,
  AppropriatenessAssessmentQuestionFive,
  AppropriatenessAssessmentQuestionSix,
  AppropriatenessAssessmentQuestionSeven,
  AppropriatenessAssessmentQuestionEight,
  AppropriatenessAssessmentQuestionNine,
  AppropriatenessAssessmentQuestionTen,
  AppropriatenessAssessmentQuestionEleven,
  AppropriatenessAssessmentQuestionTwelve,
  AppropriatenessAssessmentDefault,
} from '../assets/icons'
import { SPACE } from '../styles/layout'

const topicIcons: Record<string, FC<SVGProps<SVGSVGElement>>> = {
  ROLE_OF_BUSINESS_AND_SCOPE_OF_SERVICES: AppropriatenessAssessmentQuestionOne,
  CLIENT_RIGHTS_AND_BUSINESS_OBLIGATIONS: AppropriatenessAssessmentQuestionTwo,
  RISK_OF_MONEY_LOSS: AppropriatenessAssessmentQuestionThree,
  COMPLEXITY_OF_CRYPTO_INVESTMENTS: AppropriatenessAssessmentQuestionFour,
  CRYPTO_VOLATILITY: AppropriatenessAssessmentQuestionFive,
  OPERATIONAL_RISKS: AppropriatenessAssessmentQuestionSix,
  BUSINESS_INSOLVENCY_OR_FAILURE: AppropriatenessAssessmentQuestionSeven,
  OPERATIONAL_OUTAGE_RISK: AppropriatenessAssessmentQuestionEight,
  REGULATORY_STATUS: AppropriatenessAssessmentQuestionNine,
  CLIENT_PROTECTION_AGAINST_POOR_INVESTMENTS:
    AppropriatenessAssessmentQuestionTen,
  CRYPTO_INVESTMENTS_VS_MAINSTREAM_INVESTMENTS:
    AppropriatenessAssessmentQuestionEleven,
  INVESTMENT_DIVERSIFICATION_AND_10_PERCENT_MAX_RULE:
    AppropriatenessAssessmentQuestionTwelve,
}

const AppropriatenessAssessmentForm = ({
  questions,
  onComplete,
  onBack,
  loading,
}: {
  questions: AssessmentQuestion[]
  onComplete: (answers: SubmissionAnswer[]) => void
  onBack: () => void
  loading: boolean
}) => {
  const [answers, setAnswers] = useState<AnswerMap>({})

  const [currentQuestionIndex, setCurrentQuestionIndex] = useState<number>(0)

  if (currentQuestionIndex + 1 > questions.length) {
    return (
      <ZContainer style={{ justifyContent: 'center' }}>
        <CircularProgress />
      </ZContainer>
    )
  }

  const currentQuestion = questions[currentQuestionIndex]

  return (
    <ZScreenContainer>
      <Header
        title="Appropriateness Assessment"
        onBack={() => {
          if (currentQuestionIndex === 0) {
            onBack()
          } else {
            setCurrentQuestionIndex(currentQuestionIndex - 1)
          }
        }}
      />
      <div style={{ width: '100%' }}>
        <ZProgressBar
          value={((currentQuestionIndex + 1) / questions.length) * 100}
        />
      </div>

      <ZIcon
        Icon={
          topicIcons[currentQuestion.topic] ?? AppropriatenessAssessmentDefault
        }
        title="assessment question icon"
        style={{ width: '6rem', alignSelf: 'center', margin: `${SPACE.xl} 0` }}
      />

      <TreeQuestion
        key={currentQuestion.id}
        question={currentQuestion}
        answers={answers}
        onChange={(questionId, answer) =>
          setAnswers(previous => ({ ...previous, [questionId]: answer }))
        }
      />

      <ZBottomContainer>
        <ZButton
          onClick={() => {
            setCurrentQuestionIndex(currentQuestionIndex + 1)

            if (currentQuestionIndex + 1 === questions.length) {
              onComplete(
                questions.flatMap(question =>
                  answers[question.id]?.answerId
                    ? [
                        {
                          questionId: question.id,
                          answerId: answers[question.id].answerId!,
                        },
                      ]
                    : [],
                ),
              )
            }
          }}
          disabled={!answers[currentQuestion.id]?.answerId}
          loading={loading}
        >
          {currentQuestionIndex + 1 === questions.length ? 'Submit' : 'Next'}
        </ZButton>
      </ZBottomContainer>
    </ZScreenContainer>
  )
}

export default AppropriatenessAssessmentForm
